import React from 'react';
import { observable, reaction } from 'mobx';

export enum Theme {
  DAYLIGHT = 'daylight',
  MIDNIGHT = 'midnight',
}

export const GlobalThemeStore = observable.box(getThemeFromLocalStorage());
export const ThemeContext = React.createContext(Theme.MIDNIGHT);
export const ThemeProvider = ThemeContext.Provider;
export const ThemeConsumer = ThemeContext.Consumer;
export type ThemeProps = { theme: Theme };
export function withTheme<T>(Component: React.ComponentType<T & ThemeProps>) {
  const WrappedComponent: React.FunctionComponent<T> = (props: T) => (
    <ThemeConsumer>
      {theme => <Component {...props} theme={theme}/>}
    </ThemeConsumer>
  );
  WrappedComponent.displayName = Component.displayName;
  return WrappedComponent;
}

export type ThemedStyles<K extends object> = Record<Theme, K>;

export function useStyles<K extends object>(themedStyles: ThemedStyles<K>): K {
  const theme = React.useContext(ThemeContext);
  return themedStyles[theme];
}

function getThemeFromLocalStorage(): Theme {
  const theme = localStorage.getItem('theme');
  if (theme === Theme.DAYLIGHT || theme === Theme.MIDNIGHT) {
    return theme;
  }
  return Theme.MIDNIGHT;
}

function saveThemeToLocalStorage(theme: Theme) {
  localStorage.setItem('theme', theme);
}

reaction(
  () => GlobalThemeStore.get(),
  (theme) => saveThemeToLocalStorage(theme),
);
